import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import request from '../../../app/api/requests';
import { isSubscriptionForUser } from './is-subscription-for-user';
import type { Subscription, SubscriptionRootState } from './types';

export const cancelSubscriptionAutoRenewal = createAsyncThunk<
    Subscription,
    string,
    { state: SubscriptionRootState; rejectValue: string; pendingMeta: { userId: string } }
>(
    'subscription/cancelAutoRenewal',
    async (reason, { getState, requestId, signal, rejectWithValue }) => {
        // Backend requires a reason of up to 1000 UTF-16 characters.
        const normalizedReason = typeof reason === 'string' ? reason.trim() : '';
        if (!normalizedReason || normalizedReason.length > 1000) {
            return rejectWithValue('Укажите причину отмены длиной от 1 до 1000 символов.');
        }
        const userId = getState().userAuthSettings.userId;
        try {
            const { data } = await request.put<Subscription>(
                '/Subscriptions/cancel', { reason: normalizedReason }, { signal, timeout: 15000 },
            );
            const state = getState();
            if (signal.aborted || !state.userAuthSettings.isAuth || state.userAuthSettings.userId !== userId
                || state.subscription.cancellation.currentRequestId !== requestId) {
                return rejectWithValue('Сессия пользователя изменилась.');
            }
            if (!isSubscriptionForUser(data, userId) || typeof data.isAutoRenewal !== 'boolean') {
                return rejectWithValue('Получены некорректные данные подписки. Обновите данные и повторите попытку.');
            }
            // PUT already returns the authoritative snapshot. Do not reconstruct
            // dates/status locally or perform a second GET that can fail independently.
            return data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                switch (error.response?.status) {
                    case 400: return rejectWithValue('Проверьте причину отмены: требуется от 1 до 1000 символов.');
                    case 401: return rejectWithValue('Войдите в аккаунт и повторите отмену автопродления.');
                    case 404: return rejectWithValue('Подписка не найдена. Обновите данные.');
                    case 409: return rejectWithValue('Состояние подписки изменилось. Обновите данные и повторите отмену.');
                }
            }
            return rejectWithValue('Не удалось подтвердить отмену автопродления. Обновите данные или повторите попытку.');
        }
    },
    {
        condition: (_, { getState }) => {
            const { userAuthSettings: user, subscription } = getState();
            return user.isAuth && !!user.userId && !subscription.cancellation.currentRequestId;
        },
        getPendingMeta: (_, { getState }) => ({ userId: getState().userAuthSettings.userId }),
    },
);
